import LeadList from "../../pages/Crm/components/KanbanGrid/LeadList";
import Test from "../../pages/Test";
import { featureKeyMapping } from "../constants/featureKeyMapping";
import {
  features,
  authRoutes,
  type RouteConfig,
} from "../constants/featureRouteConfig";
import { FeatureConfig, UserRole } from "../global-types/global-types";
import { isDTEAllowed } from "./client-code";

export const getAccessibleRoutes = (
  featureConfig: FeatureConfig | null,
  currentUserRole: string | undefined,
  isLoggedIn: boolean = false,
  // REQUIRED, deliberately not defaulted — mirrors getAccessibleSidebarItems's fix for the same
  // class of fail-open bug (a defaulted `= true` survives only as long as every call site happens
  // to pass it; a missing required param is a compile error instead).
  hasVerbaiqChatAccess: boolean,
  hasVerbaiqAnalyticsAccess: boolean
) => {
  const accessibleRoutes: Array<{
    path: string;
    layout?: RouteConfig["layout"];
    component: React.ComponentType;
    redirectTo?: string;
  }> = [];

  if (!isLoggedIn) {
    authRoutes.forEach((route) => {
      accessibleRoutes.push({
        path: route.path,
        component: route.component,
      });
    });

    accessibleRoutes.push({
      path: "*",
      component: () => null,
      redirectTo: "/sign-in",
    });

    return accessibleRoutes;
  }

  if (!featureConfig) {
    return accessibleRoutes;
  }

  Object.entries(featureKeyMapping).forEach(([configKey, featureKey]) => {
    const config = featureConfig[configKey as keyof FeatureConfig];
    const feature = features[featureKey];

    if (!config || !feature) {
      return;
    }

    if (!config.enabled) {
      return;
    }

    // /dashboard/verbaIQ is gated by hasVerbaiqChatAccess alone: it already OR's the tenant's role
    // ceiling (verbaiq_config.verbaiq_chat_allowed_roles, falling back to the deprecated combined
    // allowed_roles) with the per-user grant (verbaiq_chat_access_client_codes) — see
    // resolveVerbaiqAccess. The generic allowed_roles re-check below is skipped for vf_copilot so
    // it can't re-impose an AND on top of that OR.
    //
    // "analytics" (featureKey) is skipped for the same reason, but only partially: it still owns
    // the plain /dashboard/analytics route, which keeps its own role check further down (via
    // route.allowedRoles). The nested verbaiq_analytics route already has its own independent
    // OR'd gate (hasVerbaiqAnalyticsAccess, checked per-route above) and must not be blocked by
    // analytics_config's role list — analytics_config carries no allowed_roles at all, so that
    // list is really just feature.allowedRoles, unrelated to VerbaIQ's tenant role ceiling.
    if (featureKey === "vf_copilot") {
      if (!hasVerbaiqChatAccess) {
        return;
      }
    } else if (featureKey !== "analytics") {
      let allowedRoles: UserRole[] = [];

      // If the config has allowed_roles and it's not empty, use them; otherwise use the default from featureRouteConfig
      if (
        "allowed_roles" in config &&
        Array.isArray(config.allowed_roles) &&
        config.allowed_roles.length > 0
      ) {
        allowedRoles = config.allowed_roles as UserRole[];
      } else {
        allowedRoles = feature.allowedRoles;
      }

      if (currentUserRole && !allowedRoles.includes(currentUserRole as UserRole)) {
        return;
      }
    }

    if (featureKey === "dte" && !isDTEAllowed(currentUserRole)) {
      return;
    }

    const accessibleFeatureRoutes = feature?.routes || [];

    accessibleFeatureRoutes?.forEach((route) => {
      // "verbaiq_analytics" lives under the "analytics" featureKey (alongside the unrelated
      // /dashboard/analytics route), not "vf_copilot" — so it needs its own per-route grant check
      // here rather than a featureKey-wide gate. Mirrors getAccessibleSidebarItems, which checks
      // this same per-user access via item.id.
      if (route.id === "verbaiq_analytics") {
        if (!hasVerbaiqAnalyticsAccess) {
          return;
        }
      } else if (
        featureKey === "analytics" &&
        currentUserRole &&
        Array.isArray(route.allowedRoles) &&
        !route.allowedRoles.includes(currentUserRole as UserRole)
      ) {
        // The plain /dashboard/analytics route lost its featureKey-wide role gate above (that
        // gate is now skipped for "analytics" so it can't block the sibling verbaiq_analytics
        // route) — restore the same role check here, scoped to this one route.
        return;
      }

      accessibleRoutes.push({
        path: route.path,
        component: route.component as React.ComponentType,
        layout: route.layout,
      });
    });
  });
  accessibleRoutes.push({
    path: "/dashboard/test",
    component: Test,
  });
  accessibleRoutes.push({
    path: "/dashboard/crm/list",
    component: LeadList,
  });

  const defaultRoute = accessibleRoutes.find((route) =>
    route.path.includes("/dashboard/communications/inbox")
  );

  const alternateRoute = accessibleRoutes.find((route) =>
    route.path.includes("/dashboard/")
  );

  if (defaultRoute) {
    accessibleRoutes.push({
      path: "*",
      component: () => null,
      redirectTo: defaultRoute.path,
    });
  } else if (alternateRoute) {
    accessibleRoutes.push({
      path: "*",
      component: () => null,
      redirectTo: alternateRoute.path,
    });
  }

  return accessibleRoutes;
};
